import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { logout } from "../actions/auth";
import { useCompetingAppNavLocked } from "../utils/surveyEntry";
import { PUBLIC_NAV_LINKS, useMembershipNav } from "../utils/membershipNav";
import { useDormantFeatures } from "../utils/useDormantFeatures";
import { User2, ChevronDown, ChevronUp, Settings, LogOut, Menu, X } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import { trackSignupCta } from "../utils/publicAnalytics";
import DopleLogo from "../containers/icons/logo.svg";
import "./Navbar.css";

const Navbar = ({ logout, isAuthenticated }) => {
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const appNavLocked = useCompetingAppNavLocked(isAuthenticated);
  const membershipActive = useMembershipNav(Boolean(isAuthenticated));
  const features = useDormantFeatures();
  const showDashboard = Boolean(membershipActive && features.dashboard);
  const showChat = Boolean(membershipActive && features.chat);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          const { exp } = jwtDecode(token);
          const now = Date.now() / 1000; // seconds
          if (exp < now) {
            logout();
            navigate("/login");
          }
        } catch (err) {
          console.warn("Invalid token: logging out");
          logout();
          navigate("/login");
        }
      }
    };

    // Run immediately on mount
    checkTokenExpiration();

    // And check every minute (configurable)
    const interval = setInterval(checkTokenExpiration, 60 * 1000);

    return () => clearInterval(interval);
  }, [logout, navigate]);


  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsNavbarVisible(false);
      } else {
        setIsNavbarVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/");
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [navigate]);

  return (
    <nav className={`navbar ${isNavbarVisible ? "visible" : "hidden"}`}>
      <div className="navbar-left">
        <Link to={appNavLocked ? "/survey" : "/"} className="logo">

        <img
          src={DopleLogo}
          alt="Dople Logo"
          className="navbar-icon"
          width={36}
          height={36}
          decoding="async"
        />
          Dople
        </Link>
        <ul className="nav-links">
          {PUBLIC_NAV_LINKS.map((item) => (
            <li key={item.to}>
              <Link to={item.to}>{item.label}</Link>
            </li>
          ))}
          {isAuthenticated && !appNavLocked ? (
            <>
              {showDashboard ? (
                <li>
                  <Link to="/dashboard">Dashboard</Link>
                </li>
              ) : null}
              {showChat ? (
                <li>
                  <Link to="/chat">Chat</Link>
                </li>
              ) : null}
              <li>
                <Link to="/trips">Trips</Link>
              </li>
            </>
          ) : null}
        </ul>
      </div>

      <div className="navbar-right">
        <button
          type="button"
          className="navbar-mobile-toggle"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-expanded={mobileMenuOpen}
          aria-label="Toggle navigation"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        {!isAuthenticated ? (
          <div className="nav-item nav-item--auth">
            <Link className="login-link" to="/login">
              Login
            </Link>
            <Link className="nav-plan-trip" to="/signup" onClick={() => trackSignupCta("nav")}>
              Plan Your Trip
            </Link>
          </div>
        ) : (
          <div className="navbar-profile">
            <button
              className="navbar-profile-pill"
              onClick={() => setProfileOpen(!profileOpen)}
            >
              <div className="navbar-profile-avatar">
                <User2 size={18} />
              </div>
              {profileOpen ? (
                <ChevronUp size={18} className="chevron" />
              ) : (
                <ChevronDown size={18} className="chevron" />
              )}
            </button>

            {profileOpen && (
              <ul className="navbar-profile-menu">
                {appNavLocked ? null : (
                  <>
                    <li onClick={() => navigate("/profile")}>
                      <Settings size={16} />
                      <span>Account</span>
                    </li>
                    {features.dashboard ? (
                      <li onClick={() => navigate("/dashboard/mymodel")}>
                        <User2 size={16} />
                        <span>My Model</span>
                      </li>
                    ) : null}
                  </>
                )}
                <li onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </li>
              </ul>
            )}
          </div>
        )}
      </div>

      <div className={`navbar-mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        {PUBLIC_NAV_LINKS.map((item) => (
          <Link key={item.to} to={item.to} onClick={() => setMobileMenuOpen(false)}>{item.label}</Link>
        ))}
        {isAuthenticated && !appNavLocked ? (
          <>
            {showDashboard ? (
              <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
            ) : null}
            {showChat ? (
              <Link to="/chat" onClick={() => setMobileMenuOpen(false)}>Chat</Link>
            ) : null}
            <Link to="/trips" onClick={() => setMobileMenuOpen(false)}>Trips</Link>
          </>
        ) : null}
        {!isAuthenticated ? (
          <>
            <Link className="login-link mobile-login-link" to="/login" onClick={() => setMobileMenuOpen(false)}>
              Login
            </Link>
            <Link className="nav-plan-trip mobile-plan-trip" to="/signup" onClick={() => { trackSignupCta("nav"); setMobileMenuOpen(false); }}>
              Plan Your Trip
            </Link>
          </>
        ) : (
          <>
            {appNavLocked ? null : (
              <>
                <button type="button" onClick={() => { setMobileMenuOpen(false); navigate("/profile"); }}>Account</button>
                {features.dashboard ? (
                  <button type="button" onClick={() => { setMobileMenuOpen(false); navigate("/dashboard/mymodel"); }}>My Model</button>
                ) : null}
              </>
            )}
            <button type="button" onClick={(e) => { setMobileMenuOpen(false); handleLogout(e); }}>Logout</button>
          </>
        )}
      </div>
    </nav>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, { logout })(Navbar);
